import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { ApiClientError } from '../api/http'
import { getSalesAnalytics } from '../analytics/salesAnalyticsApi'
import { listChannelConnections } from '../channels/channelsApi'
import type { ChannelConnection } from '../channels/types'
import type {
  SalesAnalyticsBreakdownBucket,
  SalesAnalyticsGranularity,
  SalesAnalyticsMoneyTotal,
  SalesAnalyticsParams,
  SalesAnalyticsPeriodData,
  SalesAnalyticsRange,
  SalesAnalyticsReport,
} from '../analytics/types'
import { Button, Card, Icon, I, Pill, TEXT_WRAP_STYLE, Toggle, gridColumns, mobileButtonStyle } from '../components/ui'
import { useIsMobileViewport } from '../hooks/useIsMobileViewport'

interface SalesAnalyticsPageProps {
  accessToken: string
}

type Delta = { kind: 'percent'; value: number } | { kind: 'new' }

const RANGES: SalesAnalyticsRange[] = ['TODAY', 'YESTERDAY', 'THIS_WEEK', 'THIS_MONTH', 'THIS_YEAR', 'CUSTOM']
const GRANULARITIES: SalesAnalyticsGranularity[] = ['AUTO', 'DAY', 'MONTH']

function numberLocale(locale: string) {
  return locale.startsWith('ar') ? 'ar-SA' : 'en-US'
}

function formatNumber(value: number, locale: string, maximumFractionDigits = 0) {
  return new Intl.NumberFormat(numberLocale(locale), { maximumFractionDigits }).format(value)
}

function numeric(value: number) {
  return Number.isFinite(Number(value)) ? Number(value) : 0
}

function formatMoney(value: number, locale: string, currency: string) {
  try {
    return new Intl.NumberFormat(numberLocale(locale), {
      style: 'currency',
      currency,
      currencyDisplay: 'code',
      maximumFractionDigits: 2,
    }).format(numeric(value))
  } catch {
    return `${formatNumber(numeric(value), locale, 2)} ${currency}`
  }
}

function formatLocalDate(date: string, locale: string) {
  const [year, month, day] = date.split('-').map(Number)
  if (!year || !month || !day) return date
  return new Intl.DateTimeFormat(numberLocale(locale), {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, month - 1, day, 12)))
}

function formatInstant(value: string, locale: string) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return new Intl.DateTimeFormat(numberLocale(locale), {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date)
}

function browserTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

function todayInTimeZone(timeZone: string) {
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(new Date())
    const value = Object.fromEntries(parts.map(part => [part.type, part.value]))
    return `${value.year}-${value.month}-${value.day}`
  } catch {
    return new Date().toISOString().slice(0, 10)
  }
}

function inclusiveDayCount(from: string, to: string) {
  const start = Date.parse(`${from}T00:00:00Z`)
  const end = Date.parse(`${to}T00:00:00Z`)
  if (!Number.isFinite(start) || !Number.isFinite(end)) return Number.NaN
  return Math.round((end - start) / 86_400_000) + 1
}

function comparisonDelta(current: number, previous: number): Delta {
  const currentValue = numeric(current)
  const previousValue = numeric(previous)
  if (previousValue === 0 && currentValue !== 0) return { kind: 'new' }
  if (previousValue === 0) return { kind: 'percent', value: 0 }
  return { kind: 'percent', value: ((currentValue - previousValue) / Math.abs(previousValue)) * 100 }
}

function moneyForCurrency(totals: SalesAnalyticsMoneyTotal[], currency: string) {
  return numeric(totals.find(total => total.currency === currency)?.storedSalesTotal ?? 0)
}

function sectionHeading(title: string, subtitle?: string) {
  return (
    <div style={{ marginBottom: 14 }}>
      <h2 style={{ margin: 0, color: 'var(--fg)', fontSize: 18, lineHeight: 1.3 }}>{title}</h2>
      {subtitle && <p style={{ margin: '4px 0 0', color: 'var(--fg-muted)', fontSize: 13, lineHeight: 1.5 }}>{subtitle}</p>}
    </div>
  )
}

function DeltaBadge({ current, previous }: { current: number; previous: number }) {
  const { t, i18n } = useTranslation()
  const delta = comparisonDelta(current, previous)
  if (delta.kind === 'new') return <Pill tone="success" size="sm">{t('pages.salesAnalytics.comparison.new')}</Pill>
  const rounded = Math.round(delta.value * 10) / 10
  const tone = rounded > 0 ? 'success' : rounded < 0 ? 'danger' : 'neutral'
  return (
    <Pill tone={tone} size="sm">
      {t('pages.salesAnalytics.comparison.percent', {
        value: new Intl.NumberFormat(numberLocale(i18n.language), { signDisplay: 'exceptZero', maximumFractionDigits: 1 }).format(rounded),
      })}
    </Pill>
  )
}

function SummaryCard({
  label,
  value,
  comparisonValue,
  detail,
  icon,
}: {
  label: string
  value: string
  comparisonValue?: { current: number; previous: number }
  detail?: string
  icon: ReactNode
}) {
  return (
    <Card padding={18}>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 12, alignItems: 'start' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ color: 'var(--fg-subtle)', fontSize: 12, fontWeight: 800, ...TEXT_WRAP_STYLE }}>{label}</div>
          <div style={{ color: 'var(--fg)', fontSize: 24, lineHeight: 1.2, fontWeight: 800, marginTop: 7, fontVariantNumeric: 'tabular-nums', ...TEXT_WRAP_STYLE }}>{value}</div>
          {detail && <div style={{ color: 'var(--fg-muted)', fontSize: 12, marginTop: 5, ...TEXT_WRAP_STYLE }}>{detail}</div>}
          {comparisonValue && <div style={{ marginTop: 9 }}><DeltaBadge current={comparisonValue.current} previous={comparisonValue.previous} /></div>}
        </div>
        <span style={{ width: 34, height: 34, borderRadius: 10, display: 'grid', placeItems: 'center', color: 'var(--brand-700)', background: 'var(--brand-100)' }}>
          <Icon size={17}>{icon}</Icon>
        </span>
      </div>
    </Card>
  )
}

function periodLabel(data: SalesAnalyticsPeriodData, locale: string) {
  return `${formatLocalDate(data.period.startDate, locale)} – ${formatLocalDate(data.period.endDate, locale)}`
}

function TrendPanel({ current, locale, isMobile }: { current: SalesAnalyticsPeriodData; locale: string; isMobile: boolean }) {
  const { t } = useTranslation()
  const currencies = useMemo(() => Array.from(new Set(current.series.flatMap(point => point.salesTotalsByCurrency.map(total => total.currency)))).sort(), [current.series])
  const [currency, setCurrency] = useState('')
  const activeCurrency = currencies.includes(currency) ? currency : (currencies[0] ?? '')

  const maxOrders = Math.max(1, ...current.series.map(point => point.totalOrders))

  return (
    <Card padding={isMobile ? 14 : 20}>
      <div style={{ display: 'grid', gridTemplateColumns: gridColumns(isMobile, 'minmax(0, 1fr) minmax(180px, auto)'), gap: 12, alignItems: 'end' }}>
        {sectionHeading(t('pages.salesAnalytics.trend.title'), t('pages.salesAnalytics.trend.subtitle'))}
        <label style={{ display: 'grid', gap: 5, minWidth: 0 }}>
          <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--fg)' }}>{t('pages.salesAnalytics.trend.currency')}</span>
          <select
            aria-label={t('pages.salesAnalytics.trend.currency')}
            value={activeCurrency}
            disabled={currencies.length === 0}
            onChange={event => setCurrency(event.target.value)}
            style={{ width: '100%', border: '1px solid var(--border-default)', borderRadius: 10, paddingBlock: 8, paddingInline: 10, color: 'var(--fg)', background: 'white', font: 'inherit' }}
          >
            {currencies.length === 0 && <option value="">{t('pages.salesAnalytics.trend.noCurrency')}</option>}
            {currencies.map(code => <option key={code} value={code}>{code}</option>)}
          </select>
        </label>
      </div>

      <figure aria-labelledby="sales-analytics-trend-caption" style={{ margin: '16px 0 0' }}>
        <figcaption id="sales-analytics-trend-caption" style={{ color: 'var(--fg-muted)', fontSize: 12.5, marginBottom: 10 }}>
          {t('pages.salesAnalytics.trend.caption', { period: periodLabel(current, locale) })}
        </figcaption>
        <div
          role="img"
          aria-label={t('pages.salesAnalytics.trend.chartAria')}
          style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.max(1, current.series.length)}, minmax(8px, 1fr))`, gap: isMobile ? 3 : 7, height: 150, alignItems: 'end', paddingBlock: 8, borderBottom: '1px solid var(--border-default)' }}
        >
          {current.series.map(point => (
            <div
              key={point.bucketStart}
              title={t('pages.salesAnalytics.trend.pointTitle', { date: formatLocalDate(point.bucketStart, locale), count: formatNumber(point.totalOrders, locale) })}
              style={{ height: `${Math.max(point.totalOrders === 0 ? 2 : 8, (point.totalOrders / maxOrders) * 100)}%`, minHeight: 2, borderRadius: '6px 6px 2px 2px', background: point.cancelledOrders === point.totalOrders && point.totalOrders > 0 ? 'var(--warning)' : 'var(--brand-500)' }}
            />
          ))}
        </div>
      </figure>

      <div style={{ overflowX: 'auto', marginTop: 16 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: isMobile ? 520 : 0, fontSize: 13 }}>
          <caption style={{ textAlign: 'start', color: 'var(--fg-muted)', paddingBlockEnd: 8 }}>{t('pages.salesAnalytics.trend.tableCaption')}</caption>
          <thead>
            <tr style={{ background: 'var(--ink-50)' }}>
              {['date', 'totalOrders', 'salesOrders', 'cancelledOrders'].map(key => (
                <th key={key} style={{ textAlign: 'start', paddingBlock: 9, paddingInline: 10, color: 'var(--fg-subtle)', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                  {t(`pages.salesAnalytics.trend.columns.${key}`)}
                </th>
              ))}
              <th style={{ textAlign: 'start', paddingBlock: 9, paddingInline: 10, color: 'var(--fg-subtle)', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                {t('pages.salesAnalytics.trend.columns.storedSales', { currency: activeCurrency || t('pages.salesAnalytics.trend.noCurrency') })}
              </th>
            </tr>
          </thead>
          <tbody>
            {current.series.map(point => (
              <tr key={point.bucketStart} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                <td style={{ paddingBlock: 9, paddingInline: 10 }}>{formatLocalDate(point.bucketStart, locale)}</td>
                <td style={{ paddingBlock: 9, paddingInline: 10, fontVariantNumeric: 'tabular-nums' }}>{formatNumber(point.totalOrders, locale)}</td>
                <td style={{ paddingBlock: 9, paddingInline: 10, fontVariantNumeric: 'tabular-nums' }}>{formatNumber(point.salesOrders, locale)}</td>
                <td style={{ paddingBlock: 9, paddingInline: 10, fontVariantNumeric: 'tabular-nums' }}>{formatNumber(point.cancelledOrders, locale)}</td>
                <td style={{ paddingBlock: 9, paddingInline: 10, fontVariantNumeric: 'tabular-nums' }}>{activeCurrency ? formatMoney(moneyForCurrency(point.salesTotalsByCurrency, activeCurrency), locale, activeCurrency) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  )
}

function BestSellers({ current, locale, isMobile }: { current: SalesAnalyticsPeriodData; locale: string; isMobile: boolean }) {
  const { t } = useTranslation()

  return (
    <Card padding={0}>
      <div style={{ padding: isMobile ? 14 : 20, paddingBlockEnd: 8 }}>
        {sectionHeading(t('pages.salesAnalytics.bestSellers.title'), t('pages.salesAnalytics.bestSellers.subtitle'))}
      </div>
      {current.bestSellingItems.length === 0 ? (
        <div style={{ padding: 20, color: 'var(--fg-muted)', fontSize: 13 }}>{t('pages.salesAnalytics.bestSellers.empty')}</div>
      ) : isMobile ? (
        <div data-testid="analytics-best-sellers-mobile" style={{ display: 'grid', gap: 10, padding: 12 }}>
          {current.bestSellingItems.map(item => (
            <Card key={item.identity} padding={14}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <strong style={{ ...TEXT_WRAP_STYLE }}>{item.displayName || item.displaySku || item.identity}</strong>
                {item.identityType === 'NAME_ONLY' && <Pill tone="warning" size="sm">{t('pages.salesAnalytics.bestSellers.approximate')}</Pill>}
              </div>
              {item.displaySku && <div style={{ marginTop: 4, color: 'var(--fg-subtle)', fontFamily: 'var(--font-mono)', fontSize: 11.5, ...TEXT_WRAP_STYLE }}>{item.displaySku}</div>}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10, marginTop: 12, color: 'var(--fg-muted)', fontSize: 12 }}>
                <span><strong style={{ display: 'block', color: 'var(--fg)', fontSize: 16 }}>{formatNumber(item.unitsSold, locale)}</strong>{t('pages.salesAnalytics.bestSellers.units')}</span>
                <span><strong style={{ display: 'block', color: 'var(--fg)', fontSize: 16 }}>{formatNumber(item.orderCount, locale)}</strong>{t('pages.salesAnalytics.bestSellers.orders')}</span>
              </div>
              <div style={{ marginTop: 10, color: 'var(--fg-muted)', fontSize: 12 }}>{item.itemTotalsByCurrency.map(total => formatMoney(total.storedSalesTotal, locale, total.currency)).join(' · ') || t('pages.salesAnalytics.bestSellers.noMoney')}</div>
            </Card>
          ))}
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'var(--ink-50)' }}>
              {['item', 'units', 'orders', 'storedSales'].map(key => <th key={key} style={{ textAlign: 'start', paddingBlock: 10, paddingInline: 16, color: 'var(--fg-subtle)', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{t(`pages.salesAnalytics.bestSellers.columns.${key}`)}</th>)}
            </tr>
          </thead>
          <tbody>
            {current.bestSellingItems.map(item => (
              <tr key={item.identity} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                <td style={{ paddingBlock: 12, paddingInline: 16 }}>
                  <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', alignItems: 'center' }}>
                    <strong style={{ ...TEXT_WRAP_STYLE }}>{item.displayName || item.displaySku || item.identity}</strong>
                    {item.identityType === 'NAME_ONLY' && <Pill tone="warning" size="sm">{t('pages.salesAnalytics.bestSellers.approximate')}</Pill>}
                  </div>
                  {item.displaySku && <div style={{ marginTop: 3, color: 'var(--fg-subtle)', fontFamily: 'var(--font-mono)', fontSize: 11.5 }}>{item.displaySku}</div>}
                </td>
                <td style={{ paddingBlock: 12, paddingInline: 16 }}>{formatNumber(item.unitsSold, locale)}</td>
                <td style={{ paddingBlock: 12, paddingInline: 16 }}>{formatNumber(item.orderCount, locale)}</td>
                <td style={{ paddingBlock: 12, paddingInline: 16 }}>{item.itemTotalsByCurrency.map(total => formatMoney(total.storedSalesTotal, locale, total.currency)).join(' · ') || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  )
}

function bucketLabel(bucket: SalesAnalyticsBreakdownBucket, t: ReturnType<typeof useTranslation>['t']) {
  if (bucket.bucketKind === 'UNKNOWN') return t('pages.salesAnalytics.breakdowns.unknown')
  if (bucket.bucketKind === 'OTHER') return t('pages.salesAnalytics.breakdowns.other')
  return bucket.displayLabel
}

function BreakdownList({ title, buckets, locale }: { title: string; buckets: SalesAnalyticsBreakdownBucket[]; locale: string }) {
  const { t } = useTranslation()
  const max = Math.max(1, ...buckets.map(bucket => bucket.orderCount))

  return (
    <Card padding={16}>
      <h3 style={{ margin: 0, color: 'var(--fg)', fontSize: 15 }}>{title}</h3>
      {buckets.length === 0 ? (
        <p style={{ margin: '12px 0 0', color: 'var(--fg-muted)', fontSize: 13 }}>{t('pages.salesAnalytics.breakdowns.empty')}</p>
      ) : (
        <div style={{ display: 'grid', gap: 13, marginTop: 14 }}>
          {buckets.map(bucket => (
            <div key={`${bucket.bucketKind}-${bucket.key}-${bucket.channelConnectionId ?? ''}`} style={{ minWidth: 0 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 8, alignItems: 'baseline' }}>
                <span style={{ color: 'var(--fg)', fontSize: 13, fontWeight: 700, ...TEXT_WRAP_STYLE }}>
                  {bucketLabel(bucket, t)}
                  {bucket.provider && <span style={{ color: 'var(--fg-muted)', fontWeight: 500 }}> · {t(`channels.providers.${bucket.provider}`)}</span>}
                </span>
                <span style={{ color: 'var(--fg-muted)', fontSize: 12 }}>{t('pages.salesAnalytics.breakdowns.orderCount', { count: formatNumber(bucket.orderCount, locale) })}</span>
              </div>
              <div aria-hidden="true" style={{ height: 7, borderRadius: 999, background: 'var(--ink-100)', marginTop: 6, overflow: 'hidden' }}>
                <div style={{ width: `${(bucket.orderCount / max) * 100}%`, height: '100%', background: bucket.bucketKind === 'UNKNOWN' ? 'var(--warning)' : bucket.bucketKind === 'OTHER' ? 'var(--ink-500)' : 'var(--brand-500)', borderRadius: 999 }} />
              </div>
              {bucket.salesTotalsByCurrency.length > 0 && <div style={{ color: 'var(--fg-subtle)', fontSize: 11.5, marginTop: 4 }}>{bucket.salesTotalsByCurrency.map(total => formatMoney(total.storedSalesTotal, locale, total.currency)).join(' · ')}</div>}
            </div>
          ))}
        </div>
      )}
    </Card>
  )
}

function DataQualityNotice({ current, locale }: { current: SalesAnalyticsPeriodData; locale: string }) {
  const { t } = useTranslation()
  const quality = current.dataQuality
  const counts = [
    ['fallbackTimestamp', quality.ordersUsingFallbackTimestamp],
    ['missingMoney', quality.salesOrdersMissingUsableMoneyOrCurrency],
    ['missingPayment', quality.ordersMissingPaymentIdentity],
    ['missingShipping', quality.ordersMissingShippingIdentity],
    ['excludedLines', quality.salesLineItemsExcludedFromRanking],
  ] as const
  if (counts.every(([, count]) => count === 0)) return null

  return (
    <Card padding={16} style={{ background: 'var(--ink-50)', borderColor: 'var(--border-default)' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '22px minmax(0, 1fr)', gap: 10, alignItems: 'start' }}>
        <Icon size={18} color="var(--warning)">{I.alert}</Icon>
        <div style={{ minWidth: 0 }}>
          <h2 style={{ margin: 0, color: 'var(--fg)', fontSize: 15 }}>{t('pages.salesAnalytics.dataQuality.title')}</h2>
          <p style={{ margin: '4px 0 0', color: 'var(--fg-muted)', fontSize: 12.5, lineHeight: 1.5 }}>{t('pages.salesAnalytics.dataQuality.body')}</p>
          <ul style={{ margin: '10px 0 0', paddingInlineStart: 20, color: 'var(--fg-muted)', fontSize: 12.5 }}>
            {counts.filter(([, count]) => count > 0).map(([key, count]) => <li key={key}>{t(`pages.salesAnalytics.dataQuality.items.${key}`, { count: formatNumber(count, locale) })}</li>)}
          </ul>
        </div>
      </div>
    </Card>
  )
}

function errorMessage(error: unknown, t: ReturnType<typeof useTranslation>['t']) {
  const code = error instanceof ApiClientError ? error.code : 'REQUEST_FAILED'
  const knownCodes = new Set([
    'INVALID_ANALYTICS_RANGE',
    'ANALYTICS_RANGE_PARAMETERS_INVALID',
    'ANALYTICS_RANGE_TOO_LARGE',
    'INVALID_ANALYTICS_TIME_ZONE',
    'INVALID_ANALYTICS_GRANULARITY',
  ])
  return knownCodes.has(code) ? t(`pages.salesAnalytics.errors.codes.${code}`) : t('pages.salesAnalytics.errors.requestFailed')
}

export function SalesAnalyticsPage({ accessToken }: SalesAnalyticsPageProps) {
  const { t, i18n } = useTranslation()
  const isMobile = useIsMobileViewport()
  const locale = i18n.language
  const timeZone = useMemo(() => browserTimeZone(), [])
  const [selectedRange, setSelectedRange] = useState<SalesAnalyticsRange>('THIS_MONTH')
  const [granularity, setGranularity] = useState<SalesAnalyticsGranularity>('AUTO')
  const [compare, setCompare] = useState(false)
  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')
  const [validationError, setValidationError] = useState<string | null>(null)
  const [params, setParams] = useState<SalesAnalyticsParams>({
    range: 'THIS_MONTH',
    timeZone,
    granularity: 'AUTO',
    compare: false,
  })
  const [retryVersion, setRetryVersion] = useState(0)
  const [channelState, setChannelState] = useState<{ accessToken: string; channels: ChannelConnection[]; error: boolean }>({ accessToken: '', channels: [], error: false })
  const channelsLoading = channelState.accessToken !== accessToken
  const requestSequence = useRef(0)
  const requestKey = JSON.stringify([accessToken, params, retryVersion])
  const [loadState, setLoadState] = useState<{ requestKey: string; data: SalesAnalyticsReport | null; error: unknown | null }>({
    requestKey: '',
    data: null,
    error: null,
  })
  const report = loadState.data
  const loading = loadState.requestKey !== requestKey
  const error = loadState.requestKey === requestKey ? loadState.error : null
  const hasError = error !== null

  useEffect(() => {
    let mounted = true
    listChannelConnections(accessToken)
      .then(channels => {
        if (mounted) setChannelState({ accessToken, channels, error: false })
      })
      .catch(() => {
        if (mounted) setChannelState({ accessToken, channels: [], error: true })
      })
    return () => { mounted = false }
  }, [accessToken, retryVersion])

  useEffect(() => {
    const requestId = ++requestSequence.current
    let mounted = true

    getSalesAnalytics(accessToken, params)
      .then(nextReport => {
        if (mounted && requestId === requestSequence.current) {
          setLoadState({ requestKey, data: nextReport, error: null })
        }
      })
      .catch(nextError => {
        if (mounted && requestId === requestSequence.current) {
          setLoadState(current => ({ requestKey, data: current.data, error: nextError }))
        }
      })

    return () => {
      mounted = false
    }
  }, [accessToken, params, requestKey])

  function chooseRange(range: SalesAnalyticsRange) {
    setSelectedRange(range)
    setValidationError(null)
    if (range === 'CUSTOM') {
      const today = todayInTimeZone(timeZone)
      if (!customFrom) setCustomFrom(today)
      if (!customTo) setCustomTo(today)
      return
    }
    setParams(current => ({ range, timeZone, granularity, compare, channelConnectionId: current.channelConnectionId }))
  }

  function customDraftIsApplied() {
    return params.range === 'CUSTOM' && params.from === customFrom && params.to === customTo
  }

  function chooseGranularity(value: SalesAnalyticsGranularity) {
    setGranularity(value)
    if (selectedRange !== 'CUSTOM' || customDraftIsApplied()) {
      setParams(current => ({ ...current, granularity: value }))
    }
  }

  function chooseComparison(value: boolean) {
    setCompare(value)
    if (selectedRange !== 'CUSTOM' || customDraftIsApplied()) {
      setParams(current => ({ ...current, compare: value }))
    }
  }

  function applyCustomRange() {
    const days = inclusiveDayCount(customFrom, customTo)
    const today = todayInTimeZone(timeZone)
    if (!customFrom || !customTo) {
      setValidationError(t('pages.salesAnalytics.errors.customRequired'))
      return
    }
    if (!Number.isFinite(days) || days < 1) {
      setValidationError(t('pages.salesAnalytics.errors.customOrder'))
      return
    }
    if (customTo > today) {
      setValidationError(t('pages.salesAnalytics.errors.customFuture'))
      return
    }
    if (days > 366) {
      setValidationError(t('pages.salesAnalytics.errors.customTooLarge'))
      return
    }
    setValidationError(null)
    setParams(current => ({ range: 'CUSTOM', from: customFrom, to: customTo, timeZone, granularity, compare, channelConnectionId: current.channelConnectionId }))
  }

  const comparison = report?.comparison ?? null
  const hasReport = report !== null
  const current = report?.current ?? null
  const currentCurrencies = current?.summary.currencyTotals ?? []
  const comparisonCurrencies = comparison?.summary.currencyTotals ?? []
  const allCurrencies = Array.from(new Set([...currentCurrencies.map(total => total.currency), ...comparisonCurrencies.map(total => total.currency)])).sort()

  return (
    <div data-testid={isMobile ? 'sales-analytics-mobile' : 'sales-analytics-desktop'} style={{ display: 'grid', gap: 18, minWidth: 0 }}>
      <Card padding={isMobile ? 14 : 18}>
        <div style={{ display: 'grid', gridTemplateColumns: gridColumns(isMobile, 'minmax(0, 1fr) auto'), gap: 14, alignItems: 'start' }}>
          <div style={{ minWidth: 0 }}>
            <h2 style={{ margin: 0, fontSize: 19, color: 'var(--fg)' }}>{t('pages.salesAnalytics.title')}</h2>
            <p style={{ margin: '4px 0 0', color: 'var(--fg-muted)', fontSize: 13, lineHeight: 1.5 }}>{t('pages.salesAnalytics.subtitle')}</p>
          </div>
          <Pill tone="info" size="sm"><Icon size={13}>{I.globe}</Icon>{t('pages.salesAnalytics.timeZone', { zone: timeZone })}</Pill>
        </div>

        <label style={{ display: 'grid', gap: 5, marginTop: 16, maxWidth: isMobile ? '100%' : 280, minWidth: 0 }}>
          <span style={{ color: 'var(--fg)', fontSize: 12.5, fontWeight: 800 }}>{t('pages.salesAnalytics.filters.channel')}</span>
          <select
            value={params.channelConnectionId ?? ''}
            disabled={channelsLoading || channelState.error}
            onChange={event => setParams(current => ({ ...current, channelConnectionId: event.target.value ? Number(event.target.value) : undefined }))}
            style={{ width: '100%', minWidth: 0, border: '1px solid var(--border-default)', borderRadius: 10, paddingBlock: 8, paddingInline: 10, font: 'inherit', color: 'var(--fg)', background: 'var(--bg-card)' }}
          >
            <option value="">{t('pages.salesAnalytics.filters.allChannels')}</option>
            {!channelsLoading && channelState.channels.map(channel => <option key={channel.id} value={channel.id}>{channel.displayName}</option>)}
          </select>
        </label>
        {!channelsLoading && channelState.error && <div role="alert" style={{ marginTop: 8, color: 'var(--danger)', fontSize: 12.5 }}>
          {t('pages.salesAnalytics.filters.channelsError')}
          <Button kind="secondary" size="xs" onClick={() => setRetryVersion(value => value + 1)} style={{ marginInlineStart: 8 }}>{t('pages.salesAnalytics.states.retry')}</Button>
        </div>}

        <fieldset style={{ border: 0, padding: 0, margin: '18px 0 0', minWidth: 0 }}>
          <legend style={{ color: 'var(--fg)', fontSize: 12.5, fontWeight: 800, marginBottom: 7 }}>{t('pages.salesAnalytics.filters.range')}</legend>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
            {RANGES.map(range => (
              <Button key={range} kind={selectedRange === range ? 'primary' : 'secondary'} size="sm" ariaPressed={selectedRange === range} onClick={() => chooseRange(range)}>
                {t(`pages.salesAnalytics.ranges.${range}`)}
              </Button>
            ))}
          </div>
        </fieldset>

        {selectedRange === 'CUSTOM' && (
          <div style={{ display: 'grid', gridTemplateColumns: gridColumns(isMobile, 'minmax(150px, 1fr) minmax(150px, 1fr) auto'), gap: 10, marginTop: 14, alignItems: 'end' }}>
            <label style={{ display: 'grid', gap: 5, minWidth: 0 }}>
              <span style={{ color: 'var(--fg)', fontSize: 12.5, fontWeight: 800 }}>{t('pages.salesAnalytics.filters.from')}</span>
              <input aria-label={t('pages.salesAnalytics.filters.from')} type="date" value={customFrom} max={todayInTimeZone(timeZone)} onChange={event => setCustomFrom(event.target.value)} style={{ width: '100%', boxSizing: 'border-box', border: '1px solid var(--border-default)', borderRadius: 10, paddingBlock: 8, paddingInline: 10, font: 'inherit', color: 'var(--fg)', background: 'white' }} />
            </label>
            <label style={{ display: 'grid', gap: 5, minWidth: 0 }}>
              <span style={{ color: 'var(--fg)', fontSize: 12.5, fontWeight: 800 }}>{t('pages.salesAnalytics.filters.to')}</span>
              <input aria-label={t('pages.salesAnalytics.filters.to')} type="date" value={customTo} max={todayInTimeZone(timeZone)} onChange={event => setCustomTo(event.target.value)} style={{ width: '100%', boxSizing: 'border-box', border: '1px solid var(--border-default)', borderRadius: 10, paddingBlock: 8, paddingInline: 10, font: 'inherit', color: 'var(--fg)', background: 'white' }} />
            </label>
            <Button kind="coral" size="sm" onClick={applyCustomRange} style={mobileButtonStyle(isMobile)}>{t('pages.salesAnalytics.filters.apply')}</Button>
          </div>
        )}
        {validationError && <div role="alert" style={{ color: 'var(--danger)', fontSize: 12.5, fontWeight: 700, marginTop: 8 }}>{validationError}</div>}

        <div style={{ display: 'grid', gridTemplateColumns: gridColumns(isMobile, 'minmax(0, 1fr) auto'), gap: 14, alignItems: 'end', marginTop: 16 }}>
          <fieldset style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
            <legend style={{ color: 'var(--fg)', fontSize: 12.5, fontWeight: 800, marginBottom: 7 }}>{t('pages.salesAnalytics.filters.granularity')}</legend>
            <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
              {GRANULARITIES.map(value => <Button key={value} kind={granularity === value ? 'primary' : 'secondary'} size="sm" ariaPressed={granularity === value} onClick={() => chooseGranularity(value)}>{t(`pages.salesAnalytics.granularities.${value}`)}</Button>)}
            </div>
          </fieldset>
          <div style={{ display: 'flex', gap: 9, alignItems: 'center', justifyContent: isMobile ? 'space-between' : 'start' }}>
            <div>
              <div style={{ color: 'var(--fg)', fontSize: 12.5, fontWeight: 800 }}>{t('pages.salesAnalytics.filters.compare')}</div>
              <div style={{ color: 'var(--fg-muted)', fontSize: 11.5 }}>{t('pages.salesAnalytics.filters.compareHint')}</div>
            </div>
            <Toggle on={compare} onChange={chooseComparison} ariaLabel={t('pages.salesAnalytics.filters.compare')} />
          </div>
        </div>
        {selectedRange === 'THIS_WEEK' && <p style={{ margin: '12px 0 0', color: 'var(--fg-muted)', fontSize: 12 }}>{t('pages.salesAnalytics.sundayWeek')}</p>}
      </Card>

      {loading && !hasReport && <Card padding={20}><div aria-live="polite" style={{ color: 'var(--fg-muted)', fontWeight: 700 }}>{t('pages.salesAnalytics.states.loading')}</div></Card>}

      {hasError && !hasReport && (
        <Card padding={20}>
          <div role="alert" style={{ color: 'var(--danger)', fontWeight: 800 }}>{errorMessage(error, t)}</div>
          <Button kind="secondary" size="sm" icon={I.refresh} onClick={() => setRetryVersion(value => value + 1)} style={{ marginTop: 12 }}>{t('pages.salesAnalytics.states.retry')}</Button>
        </Card>
      )}

      {hasReport && current && (
        <>
          {(loading || hasError) && (
            <Card padding={14} style={{ borderColor: error ? 'var(--danger)' : 'var(--brand-300)', background: error ? 'var(--danger-100)' : 'var(--brand-100)' }}>
              <div aria-live="polite" style={{ display: 'flex', gap: 9, alignItems: 'center', color: error ? 'var(--danger)' : 'var(--brand-900)', fontSize: 13, fontWeight: 700, flexWrap: 'wrap' }}>
                <Icon size={15}>{error ? I.alert : I.refresh}</Icon>
                <span>{error ? t('pages.salesAnalytics.states.staleError') : t('pages.salesAnalytics.states.refreshing')}</span>
                {hasError && <Button kind="secondary" size="xs" onClick={() => setRetryVersion(value => value + 1)}>{t('pages.salesAnalytics.states.retry')}</Button>}
              </div>
            </Card>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: gridColumns(isMobile, 'minmax(0, 1fr) auto'), gap: 10, alignItems: 'center' }}>
            <div style={{ color: 'var(--fg-muted)', fontSize: 13 }}>
              <strong style={{ color: 'var(--fg)' }}>{periodLabel(current, locale)}</strong>
              <span> · {t(`pages.salesAnalytics.granularities.${report.request.resolvedGranularity}`)}</span>
              {report.request.range === 'THIS_WEEK' && <span> · {t('pages.salesAnalytics.sundayWeekShort')}</span>}
            </div>
            <div style={{ color: 'var(--fg-subtle)', fontSize: 11.5 }}>{t('pages.salesAnalytics.generatedAt', { value: formatInstant(report.generatedAt, locale) })}</div>
          </div>

          {current.summary.totalOrders === 0 ? (
            <Card padding={28}>
              <div style={{ textAlign: 'center' }}>
                <h2 style={{ margin: 0, color: 'var(--fg)', fontSize: 18 }}>{t('pages.salesAnalytics.states.emptyTitle')}</h2>
                <p style={{ margin: '6px 0 0', color: 'var(--fg-muted)', fontSize: 13 }}>{t('pages.salesAnalytics.states.emptyBody')}</p>
              </div>
            </Card>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: gridColumns(isMobile, 'repeat(4, minmax(0, 1fr))'), gap: 12 }}>
                <SummaryCard label={t('pages.salesAnalytics.summary.totalOrders')} value={formatNumber(current.summary.totalOrders, locale)} comparisonValue={comparison ? { current: current.summary.totalOrders, previous: comparison.summary.totalOrders } : undefined} icon={I.cart} />
                <SummaryCard label={t('pages.salesAnalytics.summary.salesOrders')} value={formatNumber(current.summary.salesOrders, locale)} comparisonValue={comparison ? { current: current.summary.salesOrders, previous: comparison.summary.salesOrders } : undefined} icon={I.trend} />
                <SummaryCard label={t('pages.salesAnalytics.summary.cancelledOrders')} value={formatNumber(current.summary.cancelledOrders, locale)} comparisonValue={comparison ? { current: current.summary.cancelledOrders, previous: comparison.summary.cancelledOrders } : undefined} icon={I.x} />
                <SummaryCard label={t('pages.salesAnalytics.summary.unknownOrders')} value={formatNumber(current.summary.unknownStatusOrders, locale)} comparisonValue={comparison ? { current: current.summary.unknownStatusOrders, previous: comparison.summary.unknownStatusOrders } : undefined} icon={I.alert} />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: gridColumns(isMobile, 'repeat(auto-fit, minmax(230px, 1fr))'), gap: 12 }}>
                {allCurrencies.map(currency => {
                  const currentTotal = currentCurrencies.find(total => total.currency === currency)
                  const previousTotal = comparisonCurrencies.find(total => total.currency === currency)
                  const currentValue = numeric(currentTotal?.storedSalesTotal ?? 0)
                  const previousValue = numeric(previousTotal?.storedSalesTotal ?? 0)
                  return (
                    <SummaryCard
                      key={currency}
                      label={t('pages.salesAnalytics.summary.storedSalesTotal', { currency })}
                      value={formatMoney(currentValue, locale, currency)}
                      detail={t('pages.salesAnalytics.summary.averageOrderValue', { value: formatMoney(numeric(currentTotal?.averageOrderValue ?? 0), locale, currency), count: formatNumber(currentTotal?.contributingOrderCount ?? 0, locale) })}
                      comparisonValue={comparison ? { current: currentValue, previous: previousValue } : undefined}
                      icon={I.layers}
                    />
                  )
                })}
              </div>
              {allCurrencies.length === 0 && <Card padding={16}><div style={{ color: 'var(--fg-muted)', fontSize: 13 }}>{t('pages.salesAnalytics.summary.noMoney')}</div></Card>}

              <TrendPanel current={current} locale={locale} isMobile={isMobile} />
              <BestSellers current={current} locale={locale} isMobile={isMobile} />

              <section aria-labelledby="analytics-breakdowns-title">
                <div id="analytics-breakdowns-title">{sectionHeading(t('pages.salesAnalytics.breakdowns.title'), t('pages.salesAnalytics.breakdowns.subtitle'))}</div>
                <div style={{ display: 'grid', gridTemplateColumns: gridColumns(isMobile, 'repeat(3, minmax(0, 1fr))'), gap: 12 }}>
                  <BreakdownList title={t('pages.salesAnalytics.breakdowns.payment')} buckets={current.breakdowns.paymentMethods} locale={locale} />
                  <BreakdownList title={t('pages.salesAnalytics.breakdowns.shipping')} buckets={current.breakdowns.shippingMethods} locale={locale} />
                  <BreakdownList title={t('pages.salesAnalytics.breakdowns.channels')} buckets={current.breakdowns.channels} locale={locale} />
                </div>
              </section>

              <DataQualityNotice current={current} locale={locale} />
            </>
          )}
        </>
      )}
    </div>
  )
}
